import {
  hotelServiceRequestTypeToLabelMap,
  serviceRequestTypeToLabelMap,
} from "../../../shared/utils";
import { EHotelServiceRequestType } from "../../../constants";
import { EServiceRequestType } from "../../../types/global.types";
import { IInvoiceLineItem } from "../../../api/invoices";

export interface IChargeBreakdownRow {
  key: string;
  requestType: string;
  mealType?: string;
  label: string;
  count: number;
  subtotal: number;
  hasCost: boolean;
}

export interface IChargeBreakdown {
  rows: IChargeBreakdownRow[];
  computedTotal: number;
  hasAnyCost: boolean;
}

const isBillable = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const normalizeMealType = (
  requestType: string | undefined,
  mealType: string | undefined
): string | undefined => {
  if (requestType !== EServiceRequestType.MEAL_SCAN) return undefined;
  const value = (mealType || "").trim().toUpperCase();
  return value || undefined;
};

const makeGroupKey = (requestType: string, mealType?: string) =>
  mealType ? `${requestType}::${mealType}` : requestType;

const buildRowLabel = (requestType: string, mealType?: string) => {
  const base =
    serviceRequestTypeToLabelMap[requestType as EServiceRequestType] ||
    requestType ||
    "Unspecified";
  if (!mealType) return base;
  const sitting =
    hotelServiceRequestTypeToLabelMap[mealType as EHotelServiceRequestType] ||
    mealType;
  return `${base} – ${sitting}`;
};

export const getLineItemTypeLabel = (
  requestType: string | undefined,
  mealType: string | undefined
): string => {
  if (!requestType) return "";
  return buildRowLabel(requestType, normalizeMealType(requestType, mealType));
};

export const buildChargeBreakdown = (
  lineItems: IInvoiceLineItem[]
): IChargeBreakdown => {
  const groups = new Map<
    string,
    {
      requestType: string;
      mealType?: string;
      count: number;
      subtotal: number;
      costCount: number;
    }
  >();

  (lineItems || []).forEach((item) => {
    const requestType = item?.requestType || "";
    const mealType = normalizeMealType(requestType, item?.mealType);
    const key = makeGroupKey(requestType, mealType);
    const group = groups.get(key) ?? {
      requestType,
      mealType,
      count: 0,
      subtotal: 0,
      costCount: 0,
    };

    group.count += 1;
    if (isBillable(item?.itemCost)) {
      group.subtotal += item.itemCost as number;
      group.costCount += 1;
    }
    groups.set(key, group);
  });

  const rows: IChargeBreakdownRow[] = Array.from(groups.entries()).map(
    ([key, group]) => ({
      key,
      requestType: group.requestType,
      mealType: group.mealType,
      label: buildRowLabel(group.requestType, group.mealType),
      count: group.count,
      subtotal: group.subtotal,
      hasCost: group.costCount > 0,
    })
  );

  rows.sort((a, b) => b.subtotal - a.subtotal || a.label.localeCompare(b.label));

  return {
    rows,
    computedTotal: rows.reduce((sum, row) => sum + row.subtotal, 0),
    hasAnyCost: rows.some((row) => row.hasCost),
  };
};

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

export const formatMoney = (value: number) => currency.format(value);
